<template>
  <base-slide-over ref="slideover" :title="title" size="5xl">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isLoadingData">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center">
            <base-skeleton-loader class="tw-ml-3 tw-h-3 tw-w-56" />
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-skeleton-loader class="tw-flex-grow tw-h-3" />
      </div>
    </div>
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else>
      <div v-if="filteredProgramsForDisplay.length === 0 && priceRequestItem" class="tw-text-center tw-py-8 tw-text-gray-600">
        No specific programs found in the price change request.
      </div>
      <div v-for="program in filteredProgramsForDisplay" :key="program.businessProgramId" class="tw-flex tw-items-center tw-mb-6">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex-col">
            <div class="tw-flex-col tw-my-auto">
              {{ program.name }}
              <div class="tw-text-gray-500 tw-text-xs tw-font-normal tw-mt-1">{{ formatProgramGroupName(program.programGroup) }}</div>
            </div>
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-select
          class="tw-flex-grow"
          :options="getFilteredPlans(program.name)"
          label-key="name"
          value-key="businessPlanId"
          placeholder="Select a plan"
          :value="mappings[program.businessProgramId]"
          @input="handleInput(program.businessProgramId, $event)"
          :disabled="formattedStatus == 'Approved'"
        />
      </div>

      <div class="tw-mt-8 tw-p-4 tw-bg-gray-50 tw-rounded-md" v-if="priceRequestItem">
        <p class="tw-font-semibold tw-text-base tw-mb-2">Price Change Request Details:</p>
        <p class="tw-text-sm tw-mb-1"><span class="tw-font-medium">Program Request:</span> {{ priceRequestItem.businessProgramName }}</p>
        <div class="tw-text-sm tw-mb-1" v-if="priceRequestItem.priceSuggestion">
          <p class="tw-font-medium tw-mb-1">Requested Changes:</p>

          <div v-for="suggestion in parsedPriceSuggestion" :key="suggestion.businessProgramId">
            <p>{{ formatProgramGroupName(suggestion.programGroup) }}: ${{ formatCentsToDisplay(suggestion.changePrice) }}</p>
          </div>
        </div>
        <p class="tw-text-sm tw-mb-1"><span class="tw-font-medium">Status:</span> {{ formattedStatus }}</p>
        <p class="tw-text-sm tw-mb-1 tw-flex tw-justify-between tw-items-center">
          <span><span class="tw-font-medium">Requested By:</span> {{ priceRequestItem.insertedBy }} ({{ formatDate(priceRequestItem.insertedDate) }})</span>
          <span class="tw-font-medium tw-text-red-700">Affected Count: {{ isLoadingAffectedCount ? "Loading..." : backendAffectedCount }}</span>
        </p>
      </div>
    </div>

    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-between">
        <div>
          <base-button
            v-if="formattedStatus == 'Requested'"
            :loading="isRejecting"
            class="tw-w-auto"
            @click="rejectRequest"
            type="flat"
            color="red"
            :disabled="isApproving || isRejecting"
          >
            Reject
          </base-button>
        </div>

        <div>
          <base-button type="flat" class="tw-w-auto" @click="hide()">
            Cancel
          </base-button>

          <base-button :loading="isApproving" class="tw-w-auto tw-mr-3" @click="approveRequest" :disabled="isApproving || isRejecting || formattedStatus == 'Approved'">
            Approve
          </base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { ArrowRightIcon } from "@/assets/svgs";
import { orderBy } from "lodash";
import moment from "moment";

export default {
  components: {
    ArrowRightIcon,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
    programs: {
      type: Array,
      required: true,
    },
    plans: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isLoadingMappings: false,
      businessLocation: {},
      selectedPrograms: [],
      mappings: {},
      priceRequestItem: null,
      programsInPriceRequest: [],
      backendAffectedCount: 0,
      isLoadingAffectedCount: false,
      isApproving: false,
      isRejecting: false,
    };
  },
  computed: {
    isLoadingData() {
      const isProgramsPropEmpty = this.programs.length === 0;
      const isPlansPropEmpty = this.plans.length === 0;

      const isPriceRequestProgramsPending = this.priceRequestItem && this.priceRequestItem.priceSuggestion && this.programsInPriceRequest.length === 0;

      const shouldWaitForProps = this.priceRequestItem;

      return (
        this.isLoadingMappings ||
        this.isLoadingAffectedCount ||
        (shouldWaitForProps && isProgramsPropEmpty) ||
        (shouldWaitForProps && isPlansPropEmpty) ||
        isPriceRequestProgramsPending
      );
    },
    title() {
      if (this.priceRequestItem && this.priceRequestItem.businessLocationName) {
        return `${this.priceRequestItem.businessLocationName} Price Change Request`;
      }
      return `${this.businessLocation.name} Mappings`;
    },

    filteredProgramsForDisplay() {
      if (this.priceRequestItem) {
        const programsFiltered = this.orderedPrograms.filter((program) => this.programsInPriceRequest.includes(program.businessProgramId));
        return programsFiltered;
      }
      return this.orderedPrograms;
    },
    orderedPrograms() {
      return orderBy(this.programs, ["name", "programGroup"], ["asc", "asc"]);
    },
    orderedPlans() {
      return orderBy(this.plans, ["name"], ["asc"]);
    },

    parsedPriceSuggestion() {
      try {
        if (this.priceRequestItem && this.priceRequestItem.priceSuggestion) {
          return JSON.parse(this.priceRequestItem.priceSuggestion);
        }
      } catch (e) {
        console.error("Error parsing priceSuggestion:", e);
      }
      return [];
    },
    formattedStatus() {
      if (this.priceRequestItem && this.priceRequestItem.status) {
        return this.priceRequestItem.status.charAt(0).toUpperCase() + this.priceRequestItem.status.slice(1);
      }
      return "";
    },
  },
  methods: {
    formatCentsToDisplay(cents) {
      if (typeof cents !== "number" || Number.isNaN(cents)) {
        return "0.00";
      }
      return `${(cents / 100).toFixed(2)}`;
    },
    formatDate(date) {
      if (date) return new moment.utc(date).local().format("M/D/YYYY h:mm a");
    },

    show(param1 = null, param2 = null) {
      this.priceRequestItem = null;
      this.businessLocation = {};
      this.selectedPrograms = [];
      this.mappings = {};
      this.programsInPriceRequest = [];

      this.backendAffectedCount = 0;
      this.isLoadingAffectedCount = false;

      if (param2 && param2.businessProgramPriceChangeId) {
        this.businessLocation = param1;
        this.priceRequestItem = param2;
      } else if (param1 && param1.businessProgramPriceChangeId) {
        this.priceRequestItem = param1;
        this.businessLocation = {
          businessLocationId: param1.businessLocationId,
          name: param1.businessLocationName,
        };
      } else if (param1) {
        this.businessLocation = param1;
      }

      if (this.priceRequestItem && this.priceRequestItem.priceSuggestion) {
        try {
          const parsedSuggestions = JSON.parse(this.priceRequestItem.priceSuggestion);
          this.programsInPriceRequest = parsedSuggestions.map((s) => s.businessProgramId);

          if (this.priceRequestItem.status === "approved") {
            this.backendAffectedCount = this.priceRequestItem.affectedCount || 0;
          } else if (this.businessLocation.businessLocationId && this.programsInPriceRequest.length > 0) {
            this.getBackendAffectedCount(this.businessLocation.businessLocationId, this.programsInPriceRequest);
          }
        } catch (e) {
          console.error("Failed to parse priceSuggestion from priceRequestItem:", e);
          this.programsInPriceRequest = [];
        }
      }

      if (this.businessLocation.businessLocationId) {
        this.getMappings();
      } else if (!this.priceRequestItem) {
        console.warn("Business Location ID not found for fetching mappings (not in price request mode).");
        this.isLoadingMappings = false;
      } else {
        this.isLoadingMappings = false;
      }

      this.$refs.slideover.show();
    },
    hide() {
      this.$refs.slideover.hide();
      this.priceRequestItem = null;
      this.programsInPriceRequest = [];
      this.selectedPrograms = [];
      this.mappings = {};
      this.businessLocation = {};
      this.backendAffectedCount = 0;
      this.isLoadingAffectedCount = false;
    },

    async getMappings() {
      if (!this.businessLocation.businessLocationId) {
        console.warn("Cannot fetch mappings: businessLocation.businessLocationId is missing.");
        this.isLoadingMappings = false;
        return;
      }
      try {
        this.isLoadingMappings = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings`);
        this.mappings = {};
        response.data.forEach((m) => {
          this.mappings[m.businessProgramId] = m.businessPlanId;
        });
        this.$forceUpdate();
      } finally {
        this.isLoadingMappings = false;
      }
    },

    async getBackendAffectedCount(locationId, programIds) {
      if (!locationId || !programIds || programIds.length === 0) {
        console.warn("Cannot fetch backend affected count: missing locationId or programIds.");
        this.backendAffectedCount = 0;
        return;
      }
      this.isLoadingAffectedCount = true;
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/locations/${locationId}/request-program-price-change/affected-count`, {
          params: {
            businessProgramIds: programIds,
          },
          paramsSerializer: (params) => {
            return Object.keys(params)
              .map((key) => {
                if (Array.isArray(params[key])) {
                  return params[key].map((val) => `${key}=${val}`).join("&");
                }
                return `${key}=${params[key]}`;
              })
              .join("&");
          },
        });
        this.backendAffectedCount = response.data.count;
      } catch (error) {
        console.error("Error fetching backend affected count:", error);
        this.backendAffectedCount = 0;
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to Load Affected Count",
          message: "There was an error fetching the affected count from the server.",
          error,
        });
      } finally {
        this.isLoadingAffectedCount = false;
      }
    },
    async approveRequest() {
      this.isApproving = true;
      if (!this.priceRequestItem) {
        console.error("No price request item to approve.");
        this.isApproving = false;
        return;
      }

      const unmappedPrograms = this.filteredProgramsForDisplay.filter((program) => !this.mappings[program.businessProgramId]);

      if (unmappedPrograms.length > 0) {
        const programNames = unmappedPrograms.map((p) => p.name).join(", ");
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Missing Plan Selections",
          message: `Please select a plan for all affected programs before approving. Missing for: ${programNames}`,
          type: "warning",
        });
        this.isApproving = false;
        return;
      }

      try {
        const approvedJson = [];
        const businessProgramIds = [];
        const businessPlanIds = [];

        const currentBusinessLocationId = this.businessLocation.businessLocationId || (this.priceRequestItem ? this.priceRequestItem.businessLocationId : null);
        const filteredMappingsForPayload = {};
        this.filteredProgramsForDisplay.forEach((program) => {
          const selectedPlanId = this.mappings[program.businessProgramId];
          const previousBusinessPlanId = this.mappings[program.businessProgramId];

          if (selectedPlanId) {
            approvedJson.push({
              businessLocationId: currentBusinessLocationId,
              businessProgramId: program.businessProgramId,
              previousBusinessPlanId: previousBusinessPlanId,
              businessPlanId: selectedPlanId,
              programName: program.name,
              programGroupName: program.programGroup,
            });
            businessProgramIds.push(program.businessProgramId);
            businessPlanIds.push(selectedPlanId);
            filteredMappingsForPayload[program.businessProgramId] = selectedPlanId;
          }
        });
        const payload = {
          status: "approved",
          approveJson: JSON.stringify(approvedJson),
          businessProgramIds: businessProgramIds,
          businessPlanIds: businessPlanIds,
          mappings: filteredMappingsForPayload,
          businessLocationId: currentBusinessLocationId,
        };

        await this.$axios.post(`/businesses/${this.businessId}/request-program-price-change/${this.priceRequestItem.businessProgramPriceChangeId}/approve`, payload);

        this.hide();
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Request Approved",
          message: "Price change request has been successfully approved.",
        });
        this.$emit("request-approved");
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to Approve Request",
          message: `There was an error approving the request. Please try again.`,
          error,
        });
        throw error;
      } finally {
        this.isApproving = false;
      }
    },
    async rejectRequest() {
      try {
        this.isRejecting = true;
        await this.$axios.post(`/businesses/${this.businessId}/request-program-price-change/${this.priceRequestItem.businessProgramPriceChangeId}/reject`);
        this.hide();
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Request Rejected",
          message: "Price change request has been successfully rejected.",
        });
        this.$emit("request-rejected");
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to Reject Request",
          message: `There was an error rejecting the request. Please try again.`,
          error,
        });
        throw error;
      } finally {
        this.isRejecting = false;
      }
    },
    handleInput(businessProgramId, businessPlanId) {
      if (this.selectedPrograms.includes(businessProgramId)) {
        this.selectedPrograms.forEach((programId) => {
          this.mappings[programId] = businessPlanId;
        });
      } else {
        this.mappings[businessProgramId] = businessPlanId;
      }
      this.$forceUpdate();
    },

    extractCoreProgramName(programFullName) {
      if (!programFullName) {
        return "";
      }
      let coreName = programFullName.split("(")[0].trim();

      // Regex to remove common descriptive suffixes at the end of the string,
      // ensuring word boundaries for terms like TEST, SLIT, etc.
      // The '|' separates different suffixes. '\s*' handles optional spaces.
      // '\\b' ensures a whole word match.
      coreName = coreName.replace(/\s*\b(SL Tablet|Injection|Stand-Alone|Add-On|Nasal Spray|Starter Kit|TEST|SLIT|Testing|Kit)\b$/i, "").trim();

      // Handle cases like "Alleviate - TEST" or "Alleviate Test Kit"
      coreName = coreName.replace(/\s*[-_]?\s*\b(TEST|SLIT|Injection|Kit|Testing)\b$/i, "").trim();

      // Remove "CMWL | " prefix if present
      coreName = coreName.replace(/^cmwl\s*\|\s*/i, "").trim();

      return coreName;
    },

    extractParentheticalPart(name) {
      const match = name.match(/\((.*?)\)/);
      return match ? match[1].toLowerCase().trim() : null;
    },

    extractProgramTypeSuffix(programFullName) {
      const match = programFullName.match(/\b(TEST|SLIT|INJECTION|TABLET)\b/i); // Add more as needed
      return match ? match[1].toLowerCase() : null;
    },

    getCleanedPlanNameForMatching(planFullName) {
      if (!planFullName) {
        return "";
      }
      let cleanedName = planFullName;

      cleanedName = cleanedName
        .replace(/\s*(?:\|\s*\$\d+(?:\.\d{2})?|\$\d+(?:\.\d{2})?)\s*(- Dev Plan)?(?:\s*\|\s*(?:Starter|Maintenance|On-Going|Test Plan|Pre-Rollout|Phase\s*\d))?$/gi, "")
        .trim();

      cleanedName = cleanedName.replace(/^cmwl\s*\|\s*/i, "").trim();

      return cleanedName;
    },
    getFilteredPlans(programName) {
      if (!this.orderedPlans || this.orderedPlans.length === 0) {
        return [];
      }

      const cleanProgramNameLower = programName.toLowerCase();
      const coreProgramName = this.extractCoreProgramName(programName).toLowerCase(); // e.g., "alleviate" from "Alleviate TEST"
      const programTypeSuffix = this.extractProgramTypeSuffix(programName); // e.g., "test" from "Alleviate TEST"
      const programParentheticalPart = this.extractParentheticalPart(programName); // e.g., "spiriplex 40 ige testing kit"

      return this.orderedPlans.filter((plan) => {
        const cleanPlanName = this.getCleanedPlanNameForMatching(plan.name).toLowerCase();

        // Rule 0: Specific rule for Hawaii patients
        if (cleanProgramNameLower.startsWith("hawaii patients")) {
          return cleanPlanName.startsWith("hawaii");
        }

        if (programTypeSuffix) {
          // Example: programName="Alleviate TEST", coreProgramName="alleviate", programTypeSuffix="test"
          // We look for plans like "Alleviate Test" (cleaned to "alleviate test")
          const expectedPlanNameWithSuffix = `${coreProgramName} ${programTypeSuffix}`;
          if (cleanPlanName === expectedPlanNameWithSuffix.trim()) {
            return true;
          }
        } else if (cleanPlanName === coreProgramName) {
          // If no specific suffix in programName (e.g., "Get Fit"), then an exact match to core is good.
          return true;
        }

        if (coreProgramName && cleanPlanName.startsWith(coreProgramName)) {
          if (programTypeSuffix && cleanPlanName.includes(programTypeSuffix)) {
            // Ensure the match isn't just a partial string match within another word
            const planContainsSuffixAsWord = new RegExp(`\\b${programTypeSuffix}\\b`, "i").test(cleanPlanName);
            if (planContainsSuffixAsWord) {
              return true;
            }
          } else if (!programTypeSuffix) {
            return true;
          }
        }

        const programNameWithoutParen = programName
          .split("(")[0]
          .toLowerCase()
          .trim();
        if (cleanPlanName === programNameWithoutParen) {
          return true;
        }
        if (cleanPlanName.includes(programNameWithoutParen) && programNameWithoutParen.length > 0) {
          if (programTypeSuffix === "slit" && !cleanPlanName.includes("slit")) {
            return false;
          }
          if (programTypeSuffix !== "slit" && cleanPlanName.includes("slit")) {
            return false;
          }
          return true;
        }
        if (programParentheticalPart && cleanPlanName.includes(programParentheticalPart)) {
          return true;
        }
        return false;
      });
    },
    formatProgramGroupName(groupName) {
      if (!groupName) {
        return "";
      }
      const match = groupName.match(/^(\d+\s*-\s*|)(\s*)(.*)$/);
      if (match && match[3]) {
        return match[3]; // Returns the part after the hyphen and space
      }
      return groupName; // If no match, return original string
    },
  },
};
</script>
